// src/screens/ManageUsersScreen.js
import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, SafeAreaView, ScrollView, TouchableOpacity, ActivityIndicator, TextInput, Alert } from 'react-native';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { getFunctions, httpsCallable } from 'firebase/functions'; // Import cloud functions
import { useError } from '../components/ErrorContext'; // Import useError

const COLORS = { GOLD: '#FCD000', BG: '#0b0b0b', CARD: '#101010', TEXT: '#EDEDED', DIM: '#A0A0A0', BORDER: '#1a1a1a', BLUE: '#3498DB', DANGER: '#E74C3C' };
const usd = (cents) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((cents || 0) / 100);
const fmtDate = (ts) => {
    if (!ts?.toDate) {
        if (ts && typeof ts._seconds === 'number') {
            try {
                return new Date(ts._seconds * 1000).toLocaleString('en-US', { month: 'short', day: 'numeric' });
            } catch (e) { return 'Invalid Date'; }
        }
        return 'N/A';
    }
    try {
        return ts.toDate().toLocaleString('en-US', { month: 'short', day: 'numeric' });
    } catch (e) {
        return 'Invalid Date';
    }
};

function useAdminUserQueues(isAdmin, isLoading) {
    const [queues, setQueues] = useState({
        withdrawals: [],
        loading: true,
    });

    useEffect(() => {
        if (isLoading || !isAdmin) {
            setQueues(p => ({ ...p, loading: false }));
            return;
        }

        const fetchQueues = async () => {
            try {
                const getQueues = httpsCallable(getFunctions(), 'getAdminUserQueues');
                const result = await getQueues();
                setQueues({
                    withdrawals: result.data.withdrawals || [],
                    loading: false,
                });
            } catch (error) {
                console.error("Error fetching user queues:", error);
                setQueues(p => ({ ...p, loading: false }));
            }
        };

        fetchQueues();

    }, [isAdmin, isLoading]);

    return queues;
}

export default function ManageUsersScreen({ navigation }) {
    const { user, isAdmin, isLoading } = useAdminAuth();
    const { withdrawals, loading: queuesLoading } = useAdminUserQueues(isAdmin, isLoading);
    const { showError } = useError(); 

    const [searchText, setSearchText] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    const handleSearch = useCallback(async () => {
        if (searchText.length < 3) {
            setSearchResults([]);
            return;
        }
        setIsSearching(true);
        try {
            const searchUsers = httpsCallable(getFunctions(), 'searchUsersByEmailOrName');
            const result = await searchUsers({ searchText });
            setSearchResults(result.data.users || []);
        } catch (error) {
            console.error("Error searching users:", error);
            showError("Search Error", error.message);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    }, [searchText, showError]);

    // --- MODIFICATION: This now navigates ---
    const navigateToUser = (userItem) => {
        if (!userItem || !userItem.uid) {
            showError("Navigation Error", "Cannot navigate to an invalid user.");
            return;
        }
        // This is the real navigation
        navigation.navigate('AdminUserDetailScreen', { userId: userItem.uid });
    };


    if (isLoading || !user) {
        return <SafeAreaView style={styles.safeArea}><View style={styles.centerWrap}><ActivityIndicator color={COLORS.GOLD} /></View></SafeAreaView>;
    }
    if (!isAdmin) {
        return (
            <SafeAreaView style={styles.safeArea}>
                <View style={styles.centerWrap}>
                    <Text style={styles.deniedText}>Access Denied</Text>
                    <TouchableOpacity onPress={() => navigation.navigate('Lobby')}><Text style={styles.backLink}>Go to Lobby</Text></TouchableOpacity>
                </View>
            </SafeAreaView>
        );
    }

    return (
        <SafeAreaView style={styles.safeArea}>
            <Header navigation={navigation} />
            <ScrollView contentContainerStyle={styles.container}>
                <View style={styles.header}>
                    <Text style={styles.title}>Manage Users</Text>
                    <Text style={styles.subtitle}>Process withdrawals and manage user accounts.</Text>
                </View>

                <TouchableOpacity style={styles.backButton} onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}>
                    <Text style={styles.backButtonText}>← Back to Admin Hub</Text>
                </TouchableOpacity>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>User Search</Text>
                    <View style={styles.searchContainer}>
                        <TextInput
                            style={styles.searchInput}
                            placeholder="Search by exact email or username..."
                            placeholderTextColor={COLORS.DIM}
                            value={searchText}
                            onChangeText={setSearchText}
                            onSubmitEditing={handleSearch}
                            autoCapitalize="none"
                            autoCorrect={false}
                        />
                        <TouchableOpacity style={styles.searchButton} onPress={handleSearch} disabled={isSearching}>
                            {isSearching ? <ActivityIndicator size="small" color="#000" /> : <Text style={styles.searchButtonText}>Search</Text>}
                        </TouchableOpacity>
                    </View>

                    {searchResults.length > 0 && (
                        <View style={styles.resultsContainer}>
                            <Text style={styles.resultsTitle}>Search Results</Text>
                            {searchResults.map(item => (
                                <TouchableOpacity key={item.uid} style={styles.rowItem} onPress={() => navigateToUser(item)}>
                                    <View style={{ flex: 1 }}>
                                        <Text style={styles.rowTitle}>{item.userName || 'N/A'}</Text>
                                        <Text style={styles.rowSub}>Email: <Text style={{color: COLORS.BLUE}}>{item.email}</Text></Text>
                                        <Text style={styles.rowSub}>UID: {item.uid}</Text>
                                    </View>
                                    <Text style={styles.smallBtnText}>View →</Text>
                                </TouchableOpacity>
                            ))}
                        </View>
                    )}
                </View>
                
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Pending Withdrawals</Text>
                    {queuesLoading ? <ActivityIndicator color={COLORS.GOLD} /> : (
                        withdrawals.length === 0 ? <Text style={styles.empty}>No pending withdrawals.</Text> :
                        withdrawals.map(item => (
                            <TouchableOpacity key={item.id} style={styles.rowItem} onPress={() => navigateToUser({ uid: item.requesterUid, email: item.userEmail, userName: 'N/A' })}>
                                <View style={{ flex: 1 }}>
                                    <Text style={styles.rowTitle}>{usd(item.amountCents)} Request</Text>
                                    <Text style={styles.rowSub}>User: <Text style={{color: COLORS.BLUE}}>{item.userEmail}</Text></Text>
                                    <Text style={styles.rowSub}>Requested: {fmtDate(item.requestedAt)}</Text>
                                </View>
                                <Text style={styles.smallBtnText}>Process →</Text>
                            </TouchableOpacity>
                        ))
                    )}
                </View>
            </ScrollView>
            <Footer navigation={navigation} />
            <NavBar navigation={navigation} />
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    safeArea: { flex: 1, backgroundColor: COLORS.BG },
    centerWrap: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 },
    deniedText: { color: COLORS.DANGER, fontSize: 18, fontWeight: 'bold', textAlign: 'center' },
    backLink: { color: COLORS.DIM, marginTop: 16, textDecorationLine: 'underline' },
    container: { padding: 16, paddingBottom: 120 },
    header: { alignItems: 'center', marginBottom: 16 },
    title: { fontSize: 28, fontWeight: '900', color: COLORS.GOLD, textAlign: 'center' },
    subtitle: { color: COLORS.DIM, fontSize: 14, marginTop: 4, textAlign: 'center' },
    backButton: {
        marginBottom: 20,
        alignSelf: 'flex-start',
        paddingVertical: 8,
        paddingHorizontal: 16,
        borderRadius: 6,
        backgroundColor: COLORS.CARD,
        borderWidth: 1,
        borderColor: COLORS.BORDER,
    },
    backButtonText: {
        color: COLORS.GOLD,
        fontWeight: '700',
        fontSize: 14,
    },
    section: { width: '100%', marginBottom: 24 },
    sectionTitle: { color: COLORS.TEXT, fontSize: 16, fontWeight: 'bold', marginBottom: 12, borderLeftWidth: 3, borderLeftColor: COLORS.GOLD, paddingLeft: 8 },
    rowItem: { width: '100%', padding: 12, borderRadius: 8, borderWidth: 1, borderColor: COLORS.BORDER, backgroundColor: COLORS.CARD, flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 },
    rowTitle: { color: COLORS.TEXT, fontWeight: '700', fontSize: 14 },
    rowSub: { color: COLORS.DIM, marginTop: 4, fontSize: 11 },
    smallBtnText: { color: COLORS.GOLD, fontSize: 12, fontWeight: '700' },
    empty: { color: COLORS.DIM, fontStyle: 'italic', fontSize: 13, textAlign: 'center', padding: 10 },
    searchContainer: { flexDirection: 'row', gap: 8, },
    searchInput: { flex: 1, backgroundColor: COLORS.CARD, borderColor: COLORS.BORDER, borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10, color: COLORS.TEXT, fontSize: 14, },
    searchButton: { backgroundColor: COLORS.GOLD, borderRadius: 8, paddingHorizontal: 16, alignItems: 'center', justifyContent: 'center', },
    searchButtonText: { color: '#000', fontWeight: '900', fontSize: 14, },
    resultsContainer: { marginTop: 16, },
    resultsTitle: { color: COLORS.DIM, fontSize: 12, fontWeight: '600', marginBottom: 8, },
});